// Story 8.14 (ADO-2023), D-1/D-6/D-7/D-8 — the Order footer on the Order Review page (Figma
// desktop 4978:2213, tablet 5329:14466, mobile 5324:13133): the store's own order reference and
// the totals (Total cases, Total deposit and Total excl. VAT), then "Continue shopping" and a
// second "Submit order".
//
// The reference field (AC1) is the DS `FormField`, shown whenever the review holds lines. It
// writes the cart's reference in `useOrderSubmissionStore`, which the send carries (8.6); the
// value is per cart (the depot and, Story 8.4, the account) and in memory only. Its label says "(optional)" only when the server's rule
// (`useOrderReferenceRule`) says the account does not require one; a required account reads
// "Your order reference" with `aria-required` and "Required for this account."; while the rule is
// unknown the label claims neither, and no maximum is set. The send enforces the rule itself.
// The field is read-only while a send is in flight.
//
// A refused reference (`error.field`, the typed `customer-order-reference` 400) shows on the
// field, with `aria-invalid` and the DS error alert, and focus moves to the field (which scrolls
// it into view) whichever Submit button was pressed. `SubmitOrderFailure` stays out of it, so
// the refusal is announced once.
//
// Both Submit buttons go through `useSubmitOrder`: one action, one blocked predicate, the same
// name (WCAG 3.2.4). "Continue shopping" is a link to /products, the order-sent page's target,
// styled as the frame's blue-tinted DS secondary button (it navigates, so it is a link, as on
// the order-sent page).
//
// The host's `clearCart` control (the page's Clear cart) sits beside "Continue shopping", as
// the frames draw it (desktop 4978:2230, mobile 5324:13157); the footer only places it.
//
// The totals are Total cases, "Total deposit" and "Total excl. VAT", in that order (Figma
// 4978:2237). Total excl. VAT is the host's sum of the prices the server locked on the review's
// lines, labelled so because those prices are net of VAT (Story 8.11). Total deposit (ADO-2250)
// is the host's sum of the deposits locked on the same lines, never part of Total excl. VAT,
// and is always drawn, €0.00 included (ADO-2250, D-1). Each reads UNKNOWN_TOTAL while it is
// not known.
//
// Pricing migration 18 (ADO-2249, Task 9.3; Figma set 3, DEC-13 c): above the reference row, the
// `Savings` card: "Promotions · N lines €X" (the lines it covers, AC6) and "Total saving €X" in
// green, the host's sums of the lines' savings; `€0.00` at zero, UNKNOWN_TOTAL while unknown. The
// host leaves it out (`savings` null) at the WOW entry and while no line carries a price type.
// It replaces the Epic 8 Promotions row. Below
// 768px (`useIsNarrowViewport`) everything stacks: Submit order, then Continue shopping, then
// Clear cart, as the mobile frame orders them; the DOM order follows, so the focus order
// matches what is seen.

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, FormField } from '../../design-system';
import { linesLabel, UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { useOrderReferenceRule } from '../../basket/useOrderReferenceRule';
import { useOrderSubmissionStore } from '../../basket/useOrderSubmissionStore';
import { useSubmitOrder } from '../../basket/useSubmitOrder';
import { useCartTargetAt } from '../../basket/sessionCart';
import { cartTargetKey } from '../../api/cart-target';
import linkButton from './LinkButton.module.css';
import styles from './OrderFooter.module.css';

/** The reference field's id, which a refusal moves focus to. */
export const ORDER_REFERENCE_FIELD_ID = 'order-reference';

export interface OrderFooterProps {
  readonly depotId: string | null;
  /** The review holds lines: the reference field and Submit order are shown. */
  readonly hasLines: boolean;
  /** The review's Total cases, or null while it is unknown. */
  readonly totalCases: number | null;
  /** The review's order total (its lines at their locked prices), or null while it is
   *  unknown. */
  readonly orderTotal: MoneyValue | null;
  /** ADO-2250: the deposit locked on the same lines, or null while it is unknown. Never part
   *  of `orderTotal`. */
  readonly depositTotal: MoneyValue | null;
  /** ADO-2249: the review's savings, or null when the card is not drawn (the WOW entry, or no
   *  line carries a price type, or VITE_REAL_PRICES_ENABLED is off, DEC-18). `total` and
   *  `promotionLines` are null while unknown. */
  readonly savings?: {
    readonly total: MoneyValue | null;
    readonly promotionLines: number | null;
  } | null;
  /** The host's Clear cart control, placed after "Continue shopping". */
  readonly clearCart?: ReactNode;
}

export function OrderFooter({
  depotId,
  hasLines,
  totalCases,
  orderTotal,
  depositTotal,
  savings = null,
  clearCart,
}: OrderFooterProps) {
  const savingsHeadingId = useId();
  const narrow = useIsNarrowViewport();
  const sendable = depotId !== null && hasLines;
  const totals = (
    <div className={styles.totals}>
      <div className={styles.total}>
        <span>Total cases</span>
        <span className={styles.totalValue} data-testid="cart-page-total-cases">
          {totalCases ?? UNKNOWN_TOTAL}
        </span>
      </div>
      <div className={styles.total}>
        <span>Total deposit</span>
        <span className={styles.totalValue} data-testid="cart-page-total-deposit">
          {depositTotal === null ? UNKNOWN_TOTAL : formatMoney(depositTotal)}
        </span>
      </div>
      <div className={`${styles.total} ${styles.orderTotal}`}>
        <span>Total excl. VAT</span>
        <span className={styles.orderTotalValue} data-testid="cart-page-order-total">
          {orderTotal === null ? UNKNOWN_TOTAL : formatMoney(orderTotal)}
        </span>
      </div>
    </div>
  );
  const continueShopping = (
    <Link
      to="/products"
      className={`${linkButton.linkButton} ${linkButton.secondary} ${styles.continue}`}
    >
      Continue shopping
    </Link>
  );

  return (
    <section className={styles.footer} aria-label="Order summary">
      {savings === null ? null : (
        <section className={styles.savings} aria-labelledby={savingsHeadingId}>
          <h2 id={savingsHeadingId} className={styles.savingsTitle}>
            Savings
          </h2>
          <div className={styles.savingsFigures}>
            <div className={styles.savingsFigure}>
              <span>
                {savings.promotionLines === null
                  ? 'Promotions'
                  : `Promotions · ${linesLabel(savings.promotionLines)}`}
              </span>
              <span className={styles.totalValue} data-testid="cart-page-promotion-saving">
                {savings.total === null ? UNKNOWN_TOTAL : formatMoney(savings.total)}
              </span>
            </div>
            <div className={styles.savingsFigure}>
              <span>Total saving</span>
              <span
                className={`${styles.totalValue} ${styles.savingValue}`}
                data-testid="cart-page-total-saving"
              >
                {savings.total === null ? UNKNOWN_TOTAL : formatMoney(savings.total)}
              </span>
            </div>
          </div>
        </section>
      )}
      <div className={styles.referenceRow}>
        {sendable ? <OrderReferenceField depotId={depotId} /> : null}
        {totals}
      </div>
      <div className={styles.actions}>
        {sendable && narrow ? <FooterSubmitButton depotId={depotId} /> : null}
        <div className={styles.secondaryActions}>
          {continueShopping}
          {clearCart !== undefined ? <div className={styles.clear}>{clearCart}</div> : null}
        </div>
        {sendable && !narrow ? <FooterSubmitButton depotId={depotId} /> : null}
      </div>
    </section>
  );
}

function OrderReferenceField({ depotId }: { readonly depotId: string }) {
  // Story 8.4 (ADO-2013): the rule and the reference follow the cart (the depot and the account).
  const cart = useCartTargetAt(depotId);
  const rule = useOrderReferenceRule(cart);
  const cartKey = cart === null ? depotId : cartTargetKey(cart);
  const value = useOrderSubmissionStore((s) => s.customerOrderReferenceByCart[cartKey] ?? '');
  const setReference = useOrderSubmissionStore((s) => s.setCustomerOrderReference);
  const sending = useOrderSubmissionStore((s) => s.depotId === depotId && s.phase === 'sending');
  const refusal = useOrderSubmissionStore((s) =>
    s.phase === 'failed' && s.depotId === depotId && s.error?.field === 'customerOrderReference'
      ? s.error
      : null,
  );

  // A new refusal takes the Shopper to the field, whichever Submit button was pressed. One that
  // was already there when the field mounted, or that comes back with a switch back to its depot,
  // is shown without moving focus.
  const focusedRefusal = useRef(refusal);
  useEffect(() => {
    if (refusal === null || refusal === focusedRefusal.current) return;
    focusedRefusal.current = refusal;
    document.getElementById(ORDER_REFERENCE_FIELD_ID)?.focus();
  }, [refusal]);

  const required = rule?.required === true;
  const label =
    rule !== null && !rule.required ? 'Your order reference (optional)' : 'Your order reference';

  return (
    <div className={styles.field}>
      <FormField
        id={ORDER_REFERENCE_FIELD_ID}
        label={label}
        placeholder="e.g. PO-77821"
        autoComplete="off"
        value={value}
        onChange={(event) => setReference(depotId, event.target.value)}
        readOnly={sending}
        {...(rule !== null ? { maxLength: rule.maxLength } : {})}
        {...(required ? { required: true, 'aria-required': true } : {})}
        {...(required ? { helperText: 'Required for this account.' } : {})}
        {...(refusal !== null ? { error: refusal.message } : {})}
      />
    </div>
  );
}

function FooterSubmitButton({ depotId }: { readonly depotId: string }) {
  const { send, blocked, sending } = useSubmitOrder(depotId);
  return (
    <Button
      variant="primary"
      className={styles.submit}
      data-testid="order-footer-submit-button"
      aria-disabled={blocked ? 'true' : undefined}
      onClick={send}
    >
      {sending ? 'Submitting…' : 'Submit order'}
    </Button>
  );
}
